import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import API from 'api/index';

// Upload Image
export const uploadImage = createAsyncThunk(
  "uploadImage/upload",
  async ({ formData, folderID }, { rejectWithValue, dispatch }) => {
    try {
      console.log("📡 Sending Upload Request to API:", formData);

      const response = await API.post(`images/${folderID}`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          dispatch(setUploadProgress(percentCompleted)); // ✅ Dispatch progress update
        },
      });

      dispatch(setUploadProgress(0)); // ✅ Reset progress after completion

      console.log("✅ Upload Successful. Response:", response.data);
      return response.data;
    } catch (error) {
      dispatch(setUploadProgress(0)); // Reset progress on error
      console.error("❌ Upload Image Error:", error.response?.data || error.message);
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Update Image
export const updateImage = createAsyncThunk(
  'uploadImage/update',
  async ({ folderID, imageID, imageData }, { rejectWithValue }) => {
    try {
      console.log("📡 Sending Update Request to API:", JSON.stringify(imageData));

      const response = await API.put(`images/${folderID}/${imageID}`, imageData, {
        headers: { 'Content-Type': 'application/json' },
      });

      console.log("✅ Image updated in backend:", response.data);
      return response.data;
    } catch (error) {
      console.error("❌ Update image error:", error.response?.data || error.message);
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Fetch Folder Data
export const fetchFolderData = createAsyncThunk(
  'imageGallery/fetchFolderData',
  async (folderID, { rejectWithValue }) => {
    try {
      const response = await API.get(`/images/folder/${folderID}`);
      return response.data;
    } catch (error) {
      console.error('❌ Fetch image data error:', error.response?.data || error.message);
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Delete Image
export const deleteImage = createAsyncThunk(
  'uploadImage/delete',
  async ({ folderID, imageID }, { rejectWithValue }) => {
    try {
      await API.delete(`/images/${folderID}/${imageID}`);
      return { folderID, imageID };
    } catch (error) {
      console.error('❌ Delete image error:', error.response?.data || error.message);
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Fetch Single Image Data
export const fetchImageData = createAsyncThunk(
  'uploadImage/fetchImageData',
  async (imageID, { rejectWithValue }) => {
    try {
      console.log("🖼️ Fetching Image Data for ID:", imageID);
      const response = await API.get(`/images/${imageID}`);
      return response.data.data;
    } catch (error) {
      console.error("❌ Fetch image data error:", error.response?.data || error.message);
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

const uploadImageSlice = createSlice({
  name: 'uploadImage',
  initialState: {
    status: 'idle',
    error: null,
    imageDetails: [], // ✅ Removed unnecessary `data`
    uploadProgress: 0,
  },
  reducers: {
    resetUploadProgress: (state) => {
      state.uploadProgress = 0;
    },
    setUploadProgress: (state, action) => {
      state.uploadProgress = action.payload;
    },
    addImageToState: (state, action) => {
      console.log("📸 Adding image to Redux:", action.payload);

      // ✅ Fix: Ensure HEIC images are requested as JPG
      const uploadedImage = action.payload;
      if (uploadedImage.fileName.endsWith(".heic")) {
        uploadedImage.originalFilePath = uploadedImage.originalFilePath.replace(".heic", ".jpg");
      }

      state.imageDetails.push(uploadedImage);
    },
    updateImageInState: (state, action) => {
      const updatedImageData = action.payload.updatedImage || action.payload; // ✅ Handle both response structures

      const updatedImageIndex = state.imageDetails.findIndex((img) => img._id === updatedImageData._id);
      if (updatedImageIndex !== -1) {
        state.imageDetails[updatedImageIndex] = updatedImageData;
      } else {
        state.imageDetails.push(updatedImageData); // ✅ Add if not found
      }
    },
  },
  extraReducers: (builder) => {
    builder
      // Upload Image
      .addCase(uploadImage.pending, (state) => {
        state.status = 'loading';
        state.uploadProgress = 0;
      })
      .addCase(uploadImage.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.uploadProgress = 100;

        if (!state.imageDetails.some((img) => img._id === action.payload._id)) {
          state.imageDetails.push(action.payload);
        }
      })
      .addCase(uploadImage.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload;
      })

      // Update Image
      .addCase(updateImage.fulfilled, (state, action) => {
        state.status = 'succeeded';
        const updatedImageData = action.payload.updatedImage || action.payload;
        const updatedImageIndex = state.imageDetails.findIndex((img) => img._id === updatedImageData._id);
        if (updatedImageIndex !== -1) {
          state.imageDetails[updatedImageIndex] = updatedImageData;
        } else {
          state.imageDetails.push(updatedImageData);
        }
      })
      .addCase(updateImage.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload;
      })

      .addCase(fetchFolderData.fulfilled, (state, action) => {
        state.status = 'succeeded';
        if (action.payload && Array.isArray(action.payload.images)) {
            state.imageDetails = [...action.payload.images]; // ✅ Ensure array is assigned
            console.log("✅ Redux State Updated with Images:", state.imageDetails);
        } else {
            state.imageDetails = []; // ✅ Avoid undefined errors
            console.warn("⚠️ No images received, setting imageDetails to an empty array.");
        }
    })
    

      .addCase(fetchFolderData.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload;
      })

      // Delete Image
      .addCase(deleteImage.fulfilled, (state, action) => {
        state.imageDetails = state.imageDetails.filter((image) => image._id !== action.payload.imageID);
      });
  },
});

export default uploadImageSlice.reducer;
export const { resetUploadProgress, setUploadProgress, updateImageInState } = uploadImageSlice.actions;
